// ─── src/filters/filterEngine.js ──────────────────────────────────────────────
// Shared filter + sort engine for ALL vendor categories.
// One function, one truth. Grid, List, Map, and Count all use the same output.
// ─────────────────────────────────────────────────────────────────────────────

import { normaliseFilterValue, normaliseListingField } from "./normalise.js";

// ── Apply vendor filters using the config-driven system ─────────────────────
/**
 * Filters a listings array using active filter values and filter config.
 *
 * @param {Object[]} listings    , geo-filtered listings (already scoped to country/region)
 * @param {Object}   filters     , active filter state { sort, region, specialty, tier, ... }
 * @param {Object[]} filterConfig, array of filter dimension definitions from filterConfig.js
 * @returns {Object[]} filtered listings (not sorted, call applyVendorSort separately)
 */
export function applyVendorFilters(listings, filters, filterConfig) {
  let result = listings;

  for (const dim of filterConfig) {
    const val = filters[dim.key];

    // Skip inactive filters
    if (!val || val === "All") continue;

    // Region is handled by URL navigation, not client-side filtering
    // (unless the page is at country level and region is selected in the dropdown
    //  without URL navigation, backward compat safety)
    if (dim.navigates) continue;

    const normVal = normaliseFilterValue(val);

    switch (dim.type) {
      case "select": {
        if (dim.dataField.endsWith("[]")) {
          // Array field, match if ANY element matches the normalised value
          result = matchArrayField(result, dim.dataField.replace("[]", ""), normVal);
        } else if (dim.key === "capacity") {
          // Special: capacity is a range band, not an exact match
          result = applyCapacityFilter(result, val);
        } else if (dim.key === "venueType") {
          // Special: venue type uses keyword matching across name/setting/styles/region/city
          result = applyVenueTypeFilter(result, val);
        } else if (dim.key === "priceRange") {
          // Special: price range bands matched against priceFrom numeric value
          result = applyPriceRangeFilter(result, val);
        } else if (dim.key === "guests") {
          // Special: guest capacity bands matched against capacity numeric field
          result = applyGuestsFilter(result, val);
        } else {
          // Scalar field, exact match (normalised)
          result = result.filter(v =>
            normaliseListingField(v[dim.dataField]) === normVal
          );
        }
        break;
      }

      case "range": {
        // Future: min/max range filtering
        // Not used in Phase A
        break;
      }

      case "boolean": {
        result = result.filter(v => v[dim.dataField] === true);
        break;
      }
    }
  }

  return result;
}


// ── Sort listings ────────────────────────────────────────────────────────────
/**
 * Sorts a listings array by the given sort key.
 * Returns a NEW sorted array (does not mutate input).
 *
 * @param {Object[]} listings, filtered listings
 * @param {string}   sortKey , "recommended" | "rating" | "price-low" | "price-high" | "reviews"
 * @returns {Object[]} sorted listings
 */
export function applyVendorSort(listings, sortKey) {
  const list = [...listings];

  switch (sortKey) {
    case "rating":
      return list.sort((a, b) => (b.rating || 0) - (a.rating || 0));

    case "price-low":
      return list.sort((a, b) => parsePrice(a) - parsePrice(b));

    case "price-high":
      return list.sort((a, b) => parsePrice(b) - parsePrice(a));

    case "reviews":
      return list.sort((a, b) => (b.reviews || 0) - (a.reviews || 0));

    case "recommended":
    default:
      // Featured-first, then by lwdScore, then by rating (matches venue behaviour)
      return list.sort((a, b) => {
        const fa = a.featured ? 1 : 0;
        const fb = b.featured ? 1 : 0;
        if (fb !== fa) return fb - fa;
        const sa = b.lwdScore || 0;
        const sb = a.lwdScore || 0;
        if (sa !== sb) return sa - sb;
        return (b.rating || 0) - (a.rating || 0);
      });
  }
}


// ── Extract filter options from data ─────────────────────────────────────────
/**
 * Auto-populates filter dropdown options from the listings data.
 * Returns { [filterKey]: string[] } with sorted unique values.
 *
 * @param {Object[]} listings    , geo-filtered listings
 * @param {Object[]} filterConfig, filter dimensions
 * @param {string[]} [extraRegions], additional region names (from geo.js) to merge
 * @returns {Object} { region: [...], specialty: [...], tier: [...], ... }
 */
export function extractFilterOptions(listings, filterConfig, extraRegions = []) {
  const opts = {};

  for (const dim of filterConfig) {
    if (dim.staticOptions) {
      // Use hardcoded options (e.g., capacity bands)
      opts[dim.key] = dim.staticOptions;
      continue;
    }

    if (dim.type !== "select") continue;

    const values = new Set();

    if (dim.dataField.endsWith("[]")) {
      // Array field, flatten all values
      const field = dim.dataField.replace("[]", "");
      for (const v of listings) {
        const arr = v[field];
        if (Array.isArray(arr)) {
          for (const s of arr) {
            if (s) values.add(String(s).trim());
          }
        }
      }
    } else if (dim.key === "region" && extraRegions.length) {
      // Region, merge from listings + geo.js
      for (const v of listings) {
        if (v.region) values.add(String(v.region).trim());
      }
      for (const r of extraRegions) {
        values.add(r);
      }
    } else {
      // Scalar field
      for (const v of listings) {
        const val = v[dim.dataField];
        if (val != null && val !== "") values.add(String(val).trim());
      }
    }

    let sorted = [...values].sort();

    // Optional: pin specific values to the front, ahead of the alphabetical rest
    // (e.g. featuring "wedding-planners" first in a "Vendor Type" dropdown).
    if (Array.isArray(dim.pinFirst) && dim.pinFirst.length) {
      const pinned = dim.pinFirst.filter(v => sorted.includes(v));
      const rest = sorted.filter(v => !dim.pinFirst.includes(v));
      sorted = [...pinned, ...rest];
    }

    opts[dim.key] = sorted;
  }

  return opts;
}


// ── Internal helpers ─────────────────────────────────────────────────────────

const VENUE_TYPE_KEYWORDS = {
  'Villa':           ['villa'],
  'Luxury Hotel':    ['hotel', 'palace', 'resort'],
  'Castle':          ['castle', 'castello', 'château', 'chateau'],
  'Manor House':     ['manor', ' hall', 'house'],
  'Château':         ['chateau', 'château', 'domaine'],
  'Country Estate':  ['estate', 'tenuta', 'borgo', 'masseria', 'finca'],
  'Boutique Estate': ['boutique', 'locanda', 'relais', 'inn'],
  'Beach Resort':    ['beach', 'island', 'coastal', 'bay'],
  'Mountain Resort': ['mountain', 'alpine', 'ski', 'chalet'],
};

/** The one array-field matcher. applyVendorFilters and availableVenueTypes both go
 *  through it, so "would this chip return anything?" cannot drift from "what does the
 *  filter actually do?" — they are the same code. */
function matchArrayField(listings, field, normVal) {
  return (listings || []).filter((v) => {
    const arr = v[field];
    if (!Array.isArray(arr)) return false;
    return arr.some((s) => normaliseListingField(s).includes(normVal));
  });
}

/**
 * Venue types that actually match at least `min` listings here, newest-first by count.
 *
 * `venueType` is a STATIC vocabulary, so extractFilterOptions returns all nine options
 * regardless of inventory — fine for a dropdown, wrong for a row of chips that each look
 * like a promise. Offering "Chateau" or "Beach Resort" on England and returning nothing is
 * the same defect as the invented "Michelin-Star Dining" it replaced.
 *
 * NOTE it deliberately matches styles[] via matchArrayField, NOT the VENUE_TYPE_KEYWORDS
 * name/setting/region keyword search. venueType's dataField is "styles[]", so the live
 * filter takes the array branch and applyVenueTypeFilter is unreachable for it. A first
 * version of this helper used the keyword matcher and offered "Villa" for a venue whose
 * NAME contained "villa" while its styles did not — a chip that filtered to zero.
 */
export function availableVenueTypes(listings, min = 1) {
  return Object.keys(VENUE_TYPE_KEYWORDS)
    .map((type) => ({ type, count: matchArrayField(listings, 'styles', normaliseFilterValue(type)).length }))
    .filter((r) => r.count >= min)
    .sort((a, b) => (b.count - a.count) || a.type.localeCompare(b.type))
    .map((r) => r.type);
}

function applyVenueTypeFilter(listings, venueType) {
  const keywords = VENUE_TYPE_KEYWORDS[venueType];
  if (!keywords) return listings;
  return listings.filter(v => {
    const text = [
      v.name || '',
      v.setting || '',
      ...(Array.isArray(v.styles) ? v.styles : []),
      v.region || '',
      v.city || '',
    ].join(' ').toLowerCase();
    return keywords.some(kw => text.includes(kw));
  });
}

function parsePrice(v) {
  const raw = String(v?.priceFrom || v?.priceLabel || "");
  const m = raw.match(/[\d,]+/);
  return m ? parseInt(m[0].replace(/,/g, ""), 10) : 0;
}

function parsePriceNum(v) {
  const raw = String(v?.priceFrom || v?.priceLabel || v?.priceRange || "");
  const clean = raw.replace(/[,£€$\s]/g, "").toLowerCase();
  // Handle shorthand: 50k → 50000
  const kMatch = clean.match(/^(\d+(?:\.\d+)?)k/);
  if (kMatch) return Math.round(parseFloat(kMatch[1]) * 1000);
  const m = clean.match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
}

function applyPriceRangeFilter(listings, band) {
  return listings.filter(v => {
    const p = parsePriceNum(v);
    if (p === null) return false;
    switch (band) {
      case "Under £10,000":        return p < 10000;
      case "£10,000–£25,000":      return p >= 10000  && p < 25000;
      case "£25,000–£50,000":      return p >= 25000  && p < 50000;
      case "£50,000–£100,000":     return p >= 50000  && p < 100000;
      case "£100,000+":            return p >= 100000;
      default: return true;
    }
  });
}

function applyGuestsFilter(listings, band) {
  return listings.filter(v => {
    const cap = v.capacity;
    if (!cap) return false;
    switch (band) {
      case "Up to 50 Guests":  return cap <= 50;
      case "50–100 Guests":    return cap > 50  && cap <= 100;
      case "100–200 Guests":   return cap > 100 && cap <= 200;
      case "200+ Guests":      return cap > 200;
      default: return true;
    }
  });
}

function applyCapacityFilter(listings, band) {
  switch (band) {
    case "Up to 50":
      return listings.filter(v => v.capacity && v.capacity <= 50);
    case "50\u2013100":
      return listings.filter(v => v.capacity && v.capacity > 50 && v.capacity <= 100);
    case "100\u2013200":
      return listings.filter(v => v.capacity && v.capacity > 100 && v.capacity <= 200);
    case "200+":
      return listings.filter(v => v.capacity && v.capacity > 200);
    default:
      return listings;
  }
}
